import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth, api } from "../hooks/useAuth";
import { Check, Sparkles, Plus, Minus, ArrowRight } from "lucide-react";

/* ============ Shared reveal hook ============ */
const useReveal = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.unobserve(node);
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -60px 0px", ...options }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
};

/* ============ Plan data ============ */
const PLANS = [
  {
    id: "resources",
    name: "Resources",
    priceMain: "£6.70",
    priceSuffix: "/week",
    priceSubtext: "Billed at £29.99 per month",
    tone: "standard",
    features: [
      "75+ expert-written MMI resources",
      "15 AI MMI stations with Maya every month",
      "Medical Ethics Guide",
      "Medical Case Studies Guide",
      "Interview Tips Guide",
      "NHS Hot Topics Guide",
      "NHS Constitution Guide",
      "MMI Station Techniques Guide",
      "University-specific preparation",
      "Access on desktop and mobile",
      "New resources added regularly",
    ],
  },
  {
    id: "mock",
    name: "University-Specific Mock MMI",
    priceMain: "£119.99",
    priceSuffix: "",
    priceSubtext: "One-off payment",
    tone: "standard",
    features: [
      "4-hour virtual mock MMI",
      "Two realistic 6-station circuits A & B (12 stations total)",
      "Small groups of 4 students per station",
      "Experienced interviewer panel",
      "Individual feedback for every station",
      "Personal strengths & areas for improvement",
      "Live Q&A debrief after the interview",
      "Peer observation between stations",
    ],
  },
  {
    id: "bundle",
    name: "Complete Preparation Bundle",
    priceMain: "£129.99",
    priceStrike: "£149.99",
    priceSuffix: "",
    priceSubtext: null,
    tone: "featured",
    badge: "Best Seller",
    footnote:
      "Includes one month access to Resources. Your Resources subscription will then continue monthly unless cancelled. You can manage or cancel your subscription at any time.",
    features: [
      "Everything included in Resources subscription",
      "One month full access to all study resources",
      "15 AI MMI stations with Maya",
      "One 4-hour virtual mock MMI",
      "Two realistic 6-station circuits (12 stations)",
      "Small groups of 4 students per station",
      "Experienced interviewer panel",
      "Individual feedback",
      "Personal strengths & areas for improvement",
      "Scores after each station",
      "Live Q&A debrief after the interview",
      "Peer observation between stations",
      "Save £20 compared with purchasing separately",
    ],
  },
];

/* ============ Hero ============ */
const PricingHero = () => {
  const { ref, inView } = useReveal();
  const style = {
    transform: inView ? "translate3d(0,0,0)" : "translate3d(0, 28px, 0)",
    opacity: inView ? 1 : 0,
    transition:
      "transform 900ms cubic-bezier(0.22, 1, 0.36, 1), opacity 700ms cubic-bezier(0.22, 1, 0.36, 1)",
    willChange: "transform, opacity",
  };
  return (
    <section
      className="relative overflow-hidden"
      data-testid="pricing-hero-section"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-40 -left-40 w-[720px] h-[720px] bg-[#bcd3ff] rounded-full blur-3xl opacity-90" />
        <div className="absolute -top-20 right-[-160px] w-[640px] h-[640px] bg-[#d5e4ff] rounded-full blur-3xl opacity-95" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.7),transparent_65%)]" />
      </div>
      <div
        ref={ref}
        className="max-w-5xl mx-auto px-6 pt-24 md:pt-32 pb-16 md:pb-20 text-center"
        style={style}
      >
        <span className="inline-block px-3 py-1 rounded-full bg-white/70 backdrop-blur border border-[#dbe6ff] text-[#1d4ed8] text-[12px] font-semibold tracking-[0.14em] uppercase shadow-sm">
          Pricing
        </span>
        <h1
          className="mt-6 font-semibold text-slate-900 leading-[1.02] tracking-tight text-[46px] md:text-[72px] lg:text-[84px]"
          data-testid="pricing-heading"
        >
          Preparing for medicine
          <br className="hidden md:block" /> shouldn’t be out of reach.
        </h1>
        <p className="mt-7 text-slate-500 text-[17px] md:text-[19px] leading-relaxed max-w-2xl mx-auto">
          We’ve designed simple, transparent pricing so{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-[#1d4ed8] font-medium">
              every student can access
            </span>
            <span className="absolute left-0 right-0 bottom-0.5 h-3 bg-[#dbe6ff]/70 -z-0 rounded-sm" />
          </span>{" "}
          high-quality MMI preparation.
        </p>
      </div>
    </section>
  );
};

/* ============ Accordion ============ */
const FeaturesAccordion = ({ features, isOpen, onToggle, planId }) => {
  const contentRef = useRef(null);
  const [maxH, setMaxH] = useState(0);
  useEffect(() => {
    if (!contentRef.current) return;
    setMaxH(isOpen ? contentRef.current.scrollHeight : 0);
  }, [isOpen, features]);
  return (
    <div className="mt-5" data-testid={`plan-${planId}-accordion`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-[#f5f8ff] hover:bg-[#eef4ff] transition text-left"
        data-testid={`plan-${planId}-accordion-toggle`}
        aria-expanded={isOpen}
      >
        <span className="text-[14px] font-semibold text-slate-800">
          What’s Included
        </span>
        <span className="w-7 h-7 rounded-full bg-white text-[#1d4ed8] flex items-center justify-center shadow-sm transition-transform duration-300" style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
          {isOpen ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
        </span>
      </button>
      <div
        style={{
          maxHeight: `${maxH}px`,
          transition: "max-height 260ms ease-out",
          overflow: "hidden",
        }}
      >
        <ul ref={contentRef} className="pt-4 pb-1 px-1 space-y-2.5" data-testid={`plan-${planId}-features`}>
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-[#e6f7ee] text-[#15803d] flex items-center justify-center">
                <Check className="w-3 h-3" />
              </span>
              <span className="text-[13.5px] text-slate-700 leading-relaxed">{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ============ Card ============ */
const PricingCard = ({ plan, index, openId, setOpenId, onSelect }) => {
  const { ref, inView } = useReveal();
  const featured = plan.tone === "featured";
  const style = {
    transform: inView
      ? `translate3d(0, ${featured ? "-16px" : "0"}, 0) scale(1)`
      : `translate3d(0, ${28 + index * 4}px, 0) scale(0.98)`,
    opacity: inView ? 1 : 0,
    transition: `transform 900ms cubic-bezier(0.22, 1, 0.36, 1) ${index * 90}ms, opacity 700ms cubic-bezier(0.22, 1, 0.36, 1) ${index * 90}ms`,
    willChange: "transform, opacity",
  };
  const isOpen = openId === plan.id;
  return (
    <div
      ref={ref}
      style={style}
      className="relative"
      data-testid={`plan-card-${plan.id}`}
    >
      {featured && (
        <div
          className="absolute -inset-6 -z-10 rounded-[40px] bg-gradient-to-br from-[#c9dcff] via-[#dbe6ff] to-transparent blur-3xl opacity-95"
          aria-hidden
        />
      )}
      <div
        className={`relative flex flex-col h-full rounded-[28px] bg-white border p-7 md:p-8 transition-all duration-500 hover:-translate-y-1 ${
          featured
            ? "border-[#dbe6ff] shadow-[0_50px_110px_-25px_rgba(29,78,216,0.55)] hover:shadow-[0_60px_130px_-25px_rgba(29,78,216,0.65)]"
            : "border-slate-100 shadow-[0_25px_60px_-25px_rgba(29,78,216,0.35)] hover:shadow-[0_35px_75px_-25px_rgba(29,78,216,0.5)]"
        }`}
      >
        {featured && (
          <div
            className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1d4ed8] text-white text-[11px] font-semibold tracking-wide shadow-[0_10px_25px_-8px_rgba(29,78,216,0.7)]"
            data-testid={`plan-${plan.id}-badge`}
          >
            <Sparkles className="w-3 h-3" />
            {plan.badge}
          </div>
        )}
        <div>
          <p className="text-[15px] font-semibold text-slate-800" data-testid={`plan-${plan.id}-name`}>
            {plan.name}
          </p>
          <div className="mt-4 flex items-baseline gap-3">
            {plan.priceStrike && (
              <span className="text-slate-400 line-through text-[20px]" data-testid={`plan-${plan.id}-strike`}>
                {plan.priceStrike}
              </span>
            )}
            <span
              className={`font-semibold tracking-tight leading-none ${
                featured ? "text-[#1d4ed8]" : "text-slate-900"
              } text-[42px] md:text-[52px]`}
              data-testid={`plan-${plan.id}-price`}
            >
              {plan.priceMain}
            </span>
            {plan.priceSuffix && (
              <span className="text-slate-500 text-[16px] font-medium">{plan.priceSuffix}</span>
            )}
          </div>
          {plan.priceSubtext && (
            <p className="mt-2 text-[12.5px] text-slate-400" data-testid={`plan-${plan.id}-subtext`}>
              {plan.priceSubtext}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={() => onSelect(plan.id)}
          className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-medium transition ${
            featured
              ? "bg-[#1d4ed8] text-white hover:bg-[#1e40af] shadow-[0_18px_40px_-12px_rgba(29,78,216,0.65)]"
              : "bg-white text-[#1d4ed8] border border-[#1d4ed8]/25 hover:bg-[#eef4ff]"
          }`}
          data-testid={`plan-${plan.id}-cta`}
        >
          Get Started <ArrowRight className="w-4 h-4" />
        </button>

        <FeaturesAccordion
          features={plan.features}
          isOpen={isOpen}
          onToggle={() => setOpenId(isOpen ? null : plan.id)}
          planId={plan.id}
        />

        {plan.footnote && (
          <p
            className="mt-5 text-[12px] text-slate-500 leading-relaxed"
            data-testid={`plan-${plan.id}-footnote`}
          >
            {plan.footnote}
          </p>
        )}
      </div>
    </div>
  );
};

/* ============ Grid ============ */
const PricingGrid = () => {
  const [openId, setOpenId] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const handleSelect = async (planId) => {
    if (redirecting) return;
    try {
      sessionStorage.setItem("medily.selectedPlan", planId);
    } catch (_) { /* noop */ }

    // Anonymous → send to /signup, plan is preserved for post-signup redirect.
    if (!user || typeof user !== "object") {
      navigate("/signup");
      return;
    }

    // Authenticated → fetch the Stripe Payment Link with client_reference_id and go.
    try {
      setRedirecting(true);
      const { data } = await api.get("/payments/checkout-url", { params: { plan: planId } });
      if (data?.url) {
        window.location.assign(data.url);
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
    } finally {
      setRedirecting(false);
    }
  };
  return (
    <section className="pb-28" data-testid="pricing-grid-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 items-stretch">
          {PLANS.map((plan, i) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              index={i}
              openId={openId}
              setOpenId={setOpenId}
              onSelect={handleSelect}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============ Upcoming Sessions ============ */
const STATUS_STYLES = {
  green: { block: "bg-emerald-50 text-emerald-800 border-emerald-100", key: "Seats Available" },
  yellow: { block: "bg-amber-50 text-amber-800 border-amber-100", key: "Limited Seats" },
  red: { block: "bg-rose-50 text-rose-800 border-rose-100", key: "Fully Booked" },
};

const SESSIONS = [
  {
    month: "October",
    dates: [
      { label: "Sun 11 Oct · 9am–1pm", status: "red" },
      { label: "Sat 17 Oct · 9am–1pm", status: "yellow" },
      { label: "Sat 24 Oct · 9am–1pm", status: "yellow" },
      { label: "Sat 31 Oct · 9am–1pm", status: "yellow" },
    ],
  },
  {
    month: "November",
    dates: [
      { label: "Sat 7 Nov · 9am–1pm", status: "yellow" },
      { label: "Sat 14 Nov · 9am–1pm", status: "yellow" },
      { label: "Sat 21 Nov · 9am–1pm", status: "green" },
      { label: "Sat 28 Nov · 9am–1pm", status: "green" },
    ],
  },
  {
    month: "December",
    dates: [
      { label: "Sat 5 Dec · 9am–1pm", status: "green" },
      { label: "Sat 12 Dec · 9am–1pm", status: "green" },
      { label: "Sat 19 Dec · 9am–1pm", status: "green" },
      { label: "Sun 20 Dec · 9am–1pm", status: "green" },
      { label: "Sun 27 Dec · 9am–1pm", status: "green" },
    ],
  },
];

const SessionBlock = ({ label, status }) => (
  <div
    data-testid={`session-${status}`}
    className={`rounded-xl border px-4 py-2.5 text-sm font-medium ${STATUS_STYLES[status].block}`}
  >
    {label}
  </div>
);

const UpcomingSessions = () => (
  <section className="pb-28" data-testid="upcoming-sessions-section">
    <div className="max-w-6xl mx-auto px-6">
      <div className="text-center mb-10">
        <h2 className="text-[28px] md:text-[36px] font-semibold tracking-tight text-slate-900">
          Upcoming Mock MMI Sessions
        </h2>
        <p className="mt-3 text-slate-600 text-[15px] md:text-base max-w-2xl mx-auto">
          Secure your preferred date early — each 4-hour virtual Mock MMI has limited places.
        </p>
      </div>

      <div className="grid gap-6 md:gap-8 md:grid-cols-3">
        {SESSIONS.map(({ month, dates }) => (
          <div key={month} className="rounded-2xl bg-white border border-slate-100 p-6 shadow-[0_10px_30px_-20px_rgba(15,23,42,0.15)]">
            <h3 className="text-[15px] font-semibold tracking-[0.14em] uppercase text-slate-500 mb-4">
              {month}
            </h3>
            <div className="flex flex-col gap-2.5">
              {dates.map((d) => (
                <SessionBlock key={d.label} label={d.label} status={d.status} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Status key */}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-2.5 md:gap-3" data-testid="session-status-key">
        {["green", "yellow", "red"].map((s) => (
          <div
            key={s}
            className={`rounded-xl border px-4 py-2 text-sm font-medium ${STATUS_STYLES[s].block}`}
          >
            {STATUS_STYLES[s].key}
          </div>
        ))}
      </div>

      {/* Booking information */}
      <div className="mt-10 max-w-3xl mx-auto text-center space-y-4 text-slate-600 text-[15px] leading-relaxed">
        <p>
          You will be able to book your preferred available session on Calendly after your Stripe payment has been successfully completed.
        </p>
        <p>
          Places are allocated on a first-come, first-served basis. Sessions can be booked up to 80 days in advance, so later dates will become available as they enter the booking window.
        </p>
      </div>
    </div>
  </section>
);

/* ============ Page ============ */
const Pricing = () => (
  <div className="bg-white min-h-screen">
    <Navbar />
    <PricingHero />
    <PricingGrid />
    <UpcomingSessions />
    <Footer />
  </div>
);

export default Pricing;
